import { IconButton, IconButtonProps, Tooltip } from "@mui/material";
import DownloadIcon from "assets/icons/DownloadIcon";
import { CompetencyMap, Examination, Examination_Type, Trial, Trial_Status, TrialFile } from "@generated/graphql";
import CircularLoading from "components/CircularLoading";
import Box from "@mui/material/Box";
import useGenerateTrialFile from "hooks/useGenerateTrialFile";
import { ToastContext, ToastTypeEnum } from "context/toastContext";
import { useContext, useMemo } from "react";
import downloadFile from "helpers/fetch/downloadFile";

type TTrialFile = Pick<TrialFile, "id"> & {
  file: Pick<TrialFile["file"], "id" | "url">;
};

type TTrial = Pick<Trial, "id" | "status" | "isAssessmentComplete" | "completedAt"> & {
  recruiterReportFiles?: TTrialFile[] | null | undefined;
};

type TExamination = Pick<Examination, "id" | "type" | "title" | "numericId"> & {
  trials?: TTrial[] | null;
  competencyMap?: Pick<CompetencyMap, "id" | "title">;
};

type ExaminationProps = {
  examination: TExamination;
  trial?: never;
  recruiterReportFiles?: never;
};

type TrialProps = {
  trial: TTrial;
  examination?: never;
  recruiterReportFiles?: never;
};

type RecruiterFilesProps = {
  recruiterReportFiles: TTrialFile[];
  examination?: never;
  trial?: never;
};

type IDownloadReportButtonProps = (ExaminationProps | TrialProps | RecruiterFilesProps) & {
  filename?: string;
};

type ButtonProps = IconButtonProps & { loading: boolean };

const Button = ({ loading, ...rest }: ButtonProps) =>
  loading ? (
    <CircularLoading
      size={34}
      sx={{ p: 0, display: "block" }}
    />
  ) : (
    <IconButton
      {...rest}
      sx={{ opacity: rest.disabled ? 0.5 : 1 }}>
      <DownloadIcon />
    </IconButton>
  );

const DownloadReportButton = (props: IDownloadReportButtonProps) => {
  const { examination, trial, recruiterReportFiles, filename } = props;
  const { addToast } = useContext(ToastContext);

  const resolvedTrial = useMemo(() => {
    if ("examination" in props && examination) {
      const isPersonal = examination.type === Examination_Type.Personal;
      const currentTrial = isPersonal ? examination.trials?.[0] : undefined;
      return currentTrial
        ? {
            id: currentTrial.id,
            status: currentTrial.status,
            files: currentTrial.recruiterReportFiles,
            isAssessmentComplete: currentTrial.isAssessmentComplete,
          }
        : null;
    }

    if ("trial" in props && trial) {
      return {
        id: trial.id,
        status: trial.status,
        files: trial.recruiterReportFiles,
        isAssessmentComplete: trial.isAssessmentComplete,
      };
    }

    if ("recruiterReportFiles" in props && recruiterReportFiles) {
      return {
        id: undefined,
        status: undefined,
        files: recruiterReportFiles,
        isAssessmentComplete: undefined,
      };
    }

    return null;
  }, [examination, trial, recruiterReportFiles]);

  const { onGenerateTrialFile, loading } = useGenerateTrialFile();

  const hasFiles = !!resolvedTrial?.files?.length;

  if (!hasFiles) return "-";

  const isCompleted = resolvedTrial.status === Trial_Status.Completed;
  const disabled =
    typeof resolvedTrial?.status !== "undefined"
      ? isCompleted && !resolvedTrial.isAssessmentComplete && !hasFiles
      : !hasFiles;

  const buttonClickHandle = (e: React.MouseEvent) => {
    e.stopPropagation();

    const { id } = resolvedTrial;
    if (id) {
      onGenerateTrialFile(id);
    } else {
      const file = resolvedTrial.files?.[0]?.file;

      if (file?.url) {
        downloadFile({
          url: file.url,
          filename,
          addToast,
        });
      } else {
        addToast({ type: ToastTypeEnum.ERROR, text: "Отсутствует id тестирования" });
      }
    }
  };

  return (
    <Tooltip title={disabled ? "Отчет еще не сформирован" : "Отчет по тестированию"}>
      <Box>
        <Button
          disabled={disabled}
          loading={loading}
          onClick={buttonClickHandle}
        />
      </Box>
    </Tooltip>
  );
};

export default DownloadReportButton;
